export interface Subscription {
  startDate: string;
  endDate: string;
}

export interface SeatPoolRow {
  pool: string;
  roles: string[];
  seatCount: number;
  allocatedCount: number;
  usedCount: number;
}

export type Allocation = Record<string, number>;
export type SeatTotals = Record<string, number>;

export interface OrderLine {
  pool: string;
  unitPrice: number;
  currentSeats: number;
  newSeats: number;
  deltaSeats: number;
  amount: number;
}

export interface OrderSummary {
  lines: OrderLine[];
  totalSeats: number;
  subtotal: number;
  tax: number;
  total: number;
  currentTotal: number;
  difference: number;
}

export interface SeatOrder {
  id: string;
  createdAt: string;
  seats: SeatTotals;
  deferredAllocation: Allocation;
  subtotal: number;
  tax: number;
  total: number;
  difference: number;
}

export const GENERAL_POOL = "general";
export const TAX_RATE = 0.05;
export const MAX_SEATS = 1000;

export const POOL_UNIT_PRICE: Record<string, number> = {
  general: 1200,
  projectReport: 600,
  admin: 1500,
};

export function unitPriceForPool(pool: string): number {
  return POOL_UNIT_PRICE[pool] ?? POOL_UNIT_PRICE.general;
}

export const SEAT_ORDER_STORAGE_KEY = "sfc.licenseQuota.seatOrder";
export const SUBSCRIPTION_STORAGE_KEY = "sfc.licenseQuota.subscription";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function newId(): string {
  const suffix =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `SEAT-${Date.now()}-${suffix.toUpperCase()}`;
}

const withTax = (subtotal: number) => subtotal + Math.round(subtotal * TAX_RATE);

export function buildOrder(pools: SeatPoolRow[], seats: SeatTotals): OrderSummary {
  const lines = pools.map((p) => {
    const unitPrice = unitPriceForPool(p.pool);
    const newSeats = seats[p.pool] ?? p.seatCount;
    return {
      pool: p.pool,
      unitPrice,
      currentSeats: p.seatCount,
      newSeats,
      deltaSeats: newSeats - p.seatCount,
      amount: newSeats * unitPrice,
    };
  });
  const subtotal = lines.reduce((s, l) => s + l.amount, 0);
  const tax = Math.round(subtotal * TAX_RATE);
  const currentTotal = withTax(
    lines.reduce((s, l) => s + l.currentSeats * l.unitPrice, 0)
  );
  const total = subtotal + tax;
  return {
    lines,
    totalSeats: lines.reduce((s, l) => s + l.newSeats, 0),
    subtotal,
    tax,
    total,
    currentTotal,
    difference: total - currentTotal,
  };
}

export function allocationChanges(
  current: Allocation,
  next: Allocation
): { role: string; allowedCount: number }[] {
  return Object.keys(next)
    .filter((role) => next[role] !== (current[role] ?? 0))
    .map((role) => ({ role, allowedCount: next[role] }));
}

export function canAllocate(
  seatCount: number,
  current: Allocation,
  next: Allocation
): boolean {
  const total = Object.values(next).reduce((s, n) => s + n, 0);
  const increased = Object.keys(next).some(
    (role) => next[role] > (current[role] ?? 0)
  );
  return total <= seatCount || !increased;
}

export async function getPendingSeatOrder(): Promise<SeatOrder | null> {
  const parsed = readJson<SeatOrder | null>(SEAT_ORDER_STORAGE_KEY, null);
  return parsed?.id && parsed?.seats ? parsed : null;
}

export async function savePendingSeatOrder(
  order: OrderSummary,
  deferredAllocation: Allocation
): Promise<SeatOrder> {
  const saved: SeatOrder = {
    id: newId(),
    createdAt: new Date().toISOString(),
    seats: Object.fromEntries(order.lines.map((l) => [l.pool, l.newSeats])),
    deferredAllocation,
    subtotal: order.subtotal,
    tax: order.tax,
    total: order.total,
    difference: order.difference,
  };
  writeJson(SEAT_ORDER_STORAGE_KEY, saved);
  return saved;
}

export async function clearPendingSeatOrder(): Promise<void> {
  try {
    window.localStorage.removeItem(SEAT_ORDER_STORAGE_KEY);
  } catch {}
}

export function isSeatOrderFulfilled(order: SeatOrder, pools: SeatPoolRow[]) {
  return Object.entries(order.seats).every(
    ([pool, n]) => pools.find((p) => p.pool === pool)?.seatCount === n
  );
}

export async function getSubscription(): Promise<Subscription | null> {
  const parsed = readJson<Subscription | null>(SUBSCRIPTION_STORAGE_KEY, null);
  return parsed?.startDate && parsed?.endDate ? parsed : null;
}

export async function setSubscription(sub: Subscription): Promise<void> {
  writeJson(SUBSCRIPTION_STORAGE_KEY, sub);
}
